import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import useAuth from "./hooks/useAuth";
import DashboardRoutes from "./pages/Dashboard/DashboardRoutes";
import AuthRoutes from "./pages/Auth/AuthRoutes";
import Home from "./pages/Home/Home";
import Complain from "./pages/Home/Complain";
import { Suspense, lazy } from "react";
import DashboardHome from "./pages/Dashboard/DashboardHome";
const Links = lazy(() => import("./pages/Dashboard/Links"));
const Ecards = lazy(() => import("./pages/Dashboard/Ecards"));
const PdfGen = lazy(() => import("./pages/Dashboard/PdfGen"));
const WebScreenshot = lazy(() => import("./pages/Dashboard/WebScreenshot"));
const Scraper = lazy(() => import("./pages/Dashboard/Scraper"));
const Folders = lazy(() => import("./pages/Dashboard/Folders"));
const Users = lazy(() => import("./pages/Dashboard/Users"));
const FolderDetail = lazy(() => import("./components/FolderDeatils/FolderDetail"));
const SeoDetails = lazy(() => import("./components/SeoDetails/SeoDetails"));
const SettingRoutes = lazy(() => import("./pages/Dashboard/Settings/SeettingRoutes"));
const AdminRoutes = lazy(() => import("./pages/Dashboard/Admin/AdminRoutes"));
const Seo = lazy(() => import("./pages/Dashboard/Seo"));
const EcardDesign = lazy(() => import("./components/Ecard/EcardDesign"));
const Insight = lazy(() => import("./pages/Dashboard/Insight"));
const Report = lazy(() => import("./pages/Dashboard/Report"));
const PricingPage = lazy(() => import("./pages/Dashboard/Pricing"));
const Billing = lazy(() => import("./pages/Dashboard/billing"));
const Signup = lazy(() => import("./components/Auth/Signup"));
const Sign = lazy(() => import("./components/Auth/Signin"));
const ForgotPassword = lazy(() => import("./pages/Auth/ForgotPassword"));
const VerifyAccount = lazy(() => import("./pages/Auth/VerifyAccount"));
const ChangePassword = lazy(() => import("./pages/Auth/ChangePassword"));
const ResetPassword = lazy(() => import("./pages/Auth/ResetPassword"));
const JoinAccount = lazy(() => import("./pages/Auth/JoinAccount"));
const Auth = lazy(() => import("./pages/Auth/Auth"));
const Profile = lazy(() => import("./pages/Dashboard/Settings/Profile"));
const Accounts = lazy(() => import("./pages/Dashboard/Settings/Accounts"));
const Internal = lazy(() => import("./pages/Dashboard/Settings/Internal"));
const Api = lazy(() => import("./pages/Dashboard/Settings/Api"));
const Affiliate = lazy(() => import("./pages/Dashboard/Settings/Affiliate"));
const Payments = lazy(() => import("./pages/Dashboard/Admin/Payments"));
const Plans = lazy(() => import("./pages/Dashboard/Admin/Plans"));
const Orders = lazy(() => import("./pages/Dashboard/Admin/Orders"));
const UsersList = lazy(() => import("./pages/Dashboard/Admin/UsersList"));
const ApiLogs = lazy(() => import("./pages/Dashboard/Admin/ApiLogs"));
const SetPassword = lazy(() => import("./pages/Dashboard/Admin/SetPassword"));
const VerifyEmail = lazy(() => import("./pages/Dashboard/Admin/VerifyAccount"));
const StackNote = lazy(() => import("./pages/Dashboard/StackNote"));
const StackNotesForm = lazy(() => import("./pages/Dashboard/StackNotesForm"));
const AuditLogs = lazy(() => import("./pages/Dashboard/Settings/AuditLogs"));
const Integrations = lazy(() => import("./pages/Dashboard/Integrations"));


function App() {
  const { isAuthenticated, tokenCookies, setTokenCookie } = useAuth();
  const theme = createTheme();
  const { user } = useSelector((state) => state.authen);
  const isInternal = user?.isInternal;
  const isVerified = user?.isVerified;

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <Suspense fallback={<div className="p-6 text-center">Loading...</div>}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={isAuthenticated ? <DashboardRoutes /> : <Home />} />
            <Route path="/complain" element={<Complain />} />

            {/* Auth Routes */}
            {!isAuthenticated && (
              <Route element={<AuthRoutes />}>
                <Route path="signup" element={<Auth><Signup /></Auth>} />
                <Route path="signin" element={<Auth><Sign setToken={setTokenCookie} /></Auth>} />
                <Route path="forgot-password" element={<ForgotPassword />} />
                <Route path="verify" element={<VerifyAccount />} />
                <Route path="change-password" element={<ChangePassword token={tokenCookies?.token} />} />
                <Route path="reset-password" element={<ResetPassword />} />
                <Route path="join" element={<JoinAccount />} />
              </Route>
            )}

            {/* Dashboard Routes */}
            {isAuthenticated && (
              <Route element={<DashboardRoutes />}>
                <Route index element={<DashboardHome />} />
                {isVerified && (
                  <>
                    <Route path="links" element={<Links />} />
                    <Route path="html-scraper" element={<Scraper />} />
                    <Route path="seo-analyzer" element={<Seo />} />
                    <Route path="seo-analyzer/:seoId" element={<SeoDetails />} />
                    <Route path="e-visitings" element={<Ecards />} />
                    <Route path="E-card" element={<EcardDesign />} />
                    <Route path="pdfs" element={<PdfGen />} />
                    <Route path="screenshots" element={<WebScreenshot />} />
                    <Route path="stack-notes" element={<StackNote />} />
                    <Route path="stack-note" element={<StackNotesForm />} />
                    <Route path="integrate" element={<Integrations />} />
                    <Route path="insights" element={<Insight />} />
                    <Route path="insight-reports" element={<Report />} />
                    <Route path="folders" element={<Folders />} />
                    <Route path="folders/:folderId" element={<FolderDetail />} />
                    <Route path="users" element={<Users />} />
                    <Route path="settings" element={<SettingRoutes />}>
                      <Route index element={<Profile />} />
                      <Route path="accounts" element={<Accounts />} />
                      <Route path="internal" element={<Internal />} />
                      <Route path="apis" element={<Api />} />
                      <Route path="affiliates" element={<Affiliate />} />
                      <Route path="audit-logs" element={<AuditLogs />} />
                    </Route>
                    <Route path="billings" element={<Billing />} />
                    <Route path="pricing" element={<PricingPage />} />

                    {isInternal && (
                      <Route path="admin" element={<AdminRoutes />}>
                        <Route index element={<Payments />} />
                        <Route path="plans" element={<Plans />} />
                        <Route path="orders" element={<Orders />} />
                        <Route path="users" element={<UsersList />} />
                        <Route path="api-logs" element={<ApiLogs />} />
                        <Route path="set-password" element={<SetPassword />} />
                        <Route path="verify-account" element={<VerifyEmail />} />
                      </Route>
                    )}
                  </>
                )}
              </Route>
            )}

            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Suspense>
        <ToastContainer position="top-right" autoClose={3000} />
      </Router>
    </ThemeProvider>
  );
}


export default App;

